import { I18nAndL10n } from '@application/i18n/mod';
import { AsyncSignalValue } from '@component/async.component';
import { UserSettingsService } from '@feature/settings/user_settings.service';
import { signal } from '@preact/signals-react';
import { Injectable } from '@visiba/instantiation';
import { delay } from '@visiba/std';
import { v4 } from 'uuid';

@Injectable()
export class CookiePolicyController {
	public readonly presentation = signal<AsyncSignalValue<{ chunks: ChunkVariants[] }>>({
		value: null,
		flowState: 'idle',
	});

	constructor(private readonly userSettingsService: UserSettingsService) {
		// Empty
	}

	public async load(language?: I18nAndL10n.AvailableVisibaLocales) {
		if (language == null) {
			const settings = this.userSettingsService.getSettings();
			language = settings.selectedLanguage;
		}

		const result = await this.getCookiePolicyLanguageFile(language);
		if (result.err != null) {
			this.presentation.value = {
				value: null,
				flowState: 'error',
			};

			return;
		}

		const chunks = this.intoChunks(result.ok);

		this.presentation.value = {
			value: {
				chunks: chunks,
			},
			flowState: 'accessible',
		};
	}

	private intoChunks(data: LanguageJsonModel): ChunkVariants[] {
		const parsedChunks: ChunkVariants[] = data.chunks.map((chunk) => {
			const id = v4();

			if (chunk.title != null && chunk.body == null) {
				return {
					id,
					type: 'title',
					header: chunk.title,
				};
			}

			if (chunk.table != null) {
				return {
					id,
					type: 'table',
					body: chunk.body ?? '',
					head: chunk.table.head,
					row: chunk.table.rows,
				};
			}

			if (chunk.body != null && chunk.table == null && chunk.title == null) {
				return {
					id,
					type: 'perceived',
					body: chunk.body ?? '',
				};
			}

			return {
				id,
				type: 'section',
				header: chunk.title ?? '',
				body: chunk.body ?? '',
			};
		});

		return parsedChunks;
	}

	private async getCookiePolicyLanguageFile(language: I18nAndL10n.AvailableVisibaLocales): AsyncResult<LanguageJsonModel> {
		// Artificially delays this. It's best to not show any skeletons but we
		// don't use SSR right now and having it flicker is worse.
		await delay(200);

		try {
			if (language === 'sv') {
				const request = import('./_cookie_policy_text_sv.json') as Promise<LanguageJsonModel>;
				const data = await request;

				return Ok(data);
			}

			if (language === 'fi') {
				const request = import('./_cookie_policy_text_fi.json') as Promise<LanguageJsonModel>;
				const data = await request;

				return Ok(data);
			}

			const request = import('./_cookie_policy_text_en.json') as Promise<LanguageJsonModel>;
			const data = await request;

			return Ok(data);
			// biome-ignore lint/correctness/noUnusedVariables: suppressed due to migration - should be fixed
		} catch (error) {
			return Err(new Error('Failed to load language file'));
		}
	}
}

export interface ChunkTitle {
	id: string;
	type: 'title';
	header: string;
}

export interface ChunkSection {
	id: string;
	type: 'section';
	header: string;
	body: string;
}

export interface ChunkTable {
	id: string;
	type: 'table';
	body: string;
	head: string[];
	row: string[];
}

export interface ChunkPerceived {
	id: string;
	type: 'perceived';
	body: string;
}

export type ChunkVariants = ChunkTitle | ChunkSection | ChunkTable | ChunkPerceived;

interface LanguageJsonModel {
	chunks: Array<{
		title?: string;
		body?: string;
		table?: {
			head: string[];
			rows: string[];
		};
	}>;
}
